import { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Target, CalendarClock, ArrowRight, ListChecks, CheckCircle2, ClipboardList } from "lucide-react";
import dayjs from "dayjs";

import { DashboardSection, DashboardShell } from "../../components/dashboard";
import { Button } from "../../components/ui/button";
import { cn } from "../../utils/tailwindUtils";
import useApiMethods from "../../lib/useApiMethods";
import { APPRAISAL_CYCLES, APPRAISALS } from "../../constants/api";

import EmployeePublishedSummary from "../../components/appraisal/EmployeePublishedSummary";
import RMTeamSummary from "../../components/appraisal/RMTeamSummary";
import HRGovernanceSummary from "../../components/appraisal/HRGovernanceSummary";
import useAppraisalRole from "../../hooks/useAppraisalRole";

// Server-side status strings (must match emedhrmsapi/appraisal/constants.py).
const STATUS = {
    PUBLISHED: "published",
    CLOSED: "closed",
    PENDING_EMPLOYEE_SUBMISSION: "pending_employee_submission",
    IN_PROGRESS: "in_progress",
    SENT_BACK_BY_RM: "sent_back_by_rm",
};

// Render every section a user qualifies for, so a user who is both HR and RM
// and also has their own pending appraisal sees all three blocks stacked,
// each backed by its own scoped API call (mine / team / all).
const AppraisalDashboard = () => {
    const navigate = useNavigate();
    const { fetch } = useApiMethods();
    const { user, isHR, isRM } = useAppraisalRole();

    const myEmployeeId = user?.employee_id ? String(user.employee_id) : null;

    const [loading, setLoading] = useState(true);
    const [loaded, setLoaded] = useState(false);
    const [activeCycle, setActiveCycle] = useState(null);
    const [mineList, setMineList] = useState([]);
    const [teamList, setTeamList] = useState([]);
    const [allList, setAllList] = useState([]);

    const loadData = useCallback(async () => {
        setLoading(true);
        try {
            const calls = [
                fetch(APPRAISAL_CYCLES, { status: "active" }),
                fetch(APPRAISALS, { scope: "mine" }),
            ];
            if (isRM) calls.push(fetch(APPRAISALS, { scope: "team" }));
            if (isHR) calls.push(fetch(APPRAISALS, { scope: "all" }));

            const results = await Promise.all(calls);
            let i = 0;
            const cycles = results[i++]?.data || [];
            const mine = results[i++]?.data || [];
            const team = isRM ? (results[i++]?.data || []) : [];
            const all = isHR ? (results[i++]?.data || []) : [];

            setActiveCycle(cycles[0] || null);
            setMineList(mine);
            setTeamList(team);
            setAllList(all);
        } catch (_) {
            setActiveCycle(null);
            setMineList([]);
            setTeamList([]);
            setAllList([]);
        } finally {
            setLoading(false);
            setLoaded(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isHR, isRM]);

    useEffect(() => { loadData(); }, [loadData]);

    // The self-evaluation window, stated as time remaining rather than as two
    // dates the reader has to subtract. Derived from the cycle already fetched.
    const selfEvalWindow = useMemo(() => {
        if (!activeCycle?.self_eval_end) return null;
        const end = dayjs(activeCycle.self_eval_end).endOf("day");
        const start = activeCycle.self_eval_start ? dayjs(activeCycle.self_eval_start) : null;
        const today = dayjs();
        if (start && today.isBefore(start, "day")) {
            return { tone: "muted", text: `Self-evaluation opens ${start.format("DD MMM")}` };
        }
        if (today.isAfter(end)) {
            return { tone: "closed", text: "Self-evaluation window has closed" };
        }
        const daysLeft = end.diff(today, "day");
        return {
            tone: daysLeft <= 3 ? "urgent" : "open",
            text: daysLeft === 0
                ? "Self-evaluation closes today"
                : `Self-evaluation closes in ${daysLeft} day${daysLeft === 1 ? "" : "s"}`,
        };
    }, [activeCycle]);

    // Personal published appraisal — only one owned by the logged-in user, not
    // someone else whose record happens to come back in team/all scope.
    const myPublished = useMemo(() => {
        const owned = mineList.filter((a) => {
            // Defensive: if employee.id is somehow not present, fall back to
            // treating the whole `mine` list as owned (the backend already
            // filters by employee for scope=mine).
            const empId = a.employee?.id;
            return empId == null || (myEmployeeId && String(empId) === myEmployeeId);
        });
        return owned
            .filter((a) => a.status === STATUS.PUBLISHED || a.status === STATUS.CLOSED)
            .sort((a, b) => new Date(b.published_at || b.modified_date) - new Date(a.published_at || a.modified_date))[0]
            || null;
    }, [mineList, myEmployeeId]);

    // Personal pending — the user's own appraisal that still needs their
    // submission. Shown in a small "next action" card on every role.
    const myPending = useMemo(() => {
        return mineList.find((a) => [STATUS.PENDING_EMPLOYEE_SUBMISSION, STATUS.IN_PROGRESS, STATUS.SENT_BACK_BY_RM].includes(a.status))
            || null;
    }, [mineList]);

    return (
        <DashboardShell
            Icon={Target}
            title="Appraisal Dashboard"
            subtitle={user?.name ? `Welcome, ${user.name}${user.designation ? ` (${user.designation})` : ""}` : "Performance management overview"}
            loading={loading}
            // `loaded`, not a data object: this screen has four independent
            // results and an empty one is a real answer, so "have we been round
            // once" is the only honest test for whether to keep showing the page.
            hasData={loaded}
        >
            {/* Active cycle — universal context for every role. */}
            {activeCycle && (
                <DashboardSection
                    title="Active Cycle"
                    action={isHR && (
                        <Button
                            onClick={() => navigate(`/appraisal/cycles/${activeCycle.id}`)}
                            variant="brand" className="whitespace-nowrap"
                            size="sm"
                        >
                            View Cycle <ArrowRight className="w-4 h-4 ml-1" />
                        </Button>
                    )}
                >
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{activeCycle.name}</h3>
                    <p className="text-xs text-muted-foreground mt-0.5">
                        {dayjs(activeCycle.period_start).format("DD-MM-YYYY")} – {dayjs(activeCycle.period_end).format("DD-MM-YYYY")}
                        {" · "}
                        Self-eval window {dayjs(activeCycle.self_eval_start).format("DD-MM")}–{dayjs(activeCycle.self_eval_end).format("DD-MM")}
                    </p>
                    {selfEvalWindow && (
                        <p className={cn(
                            "mt-1.5 text-xs font-medium",
                            selfEvalWindow.tone === "urgent" && "text-amber-600 dark:text-amber-300",
                            selfEvalWindow.tone === "open" && "text-emerald-600 dark:text-emerald-300",
                            (selfEvalWindow.tone === "closed" || selfEvalWindow.tone === "muted")
                                && "text-muted-foreground",
                        )}>
                            {selfEvalWindow.text}
                        </p>
                    )}
                </DashboardSection>
            )}

            {/* ── Personal section — only the logged-in user's own records ── */}
            {myPublished ? (
                <EmployeePublishedSummary appraisal={myPublished} />
            ) : myPending ? (
                /* An alert, not a section: this is the one thing on the page
                   addressed to the viewer personally, so it keeps a colour the
                   neutral section cards deliberately do not have. */
                <div className="rounded-lg border border-blue-200 dark:border-blue-900/40 bg-blue-50/60 dark:bg-blue-900/15 p-3.5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div className="min-w-0">
                        <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-wide text-blue-700 dark:text-blue-300 mb-1">
                            <ClipboardList className="w-3.5 h-3.5" />
                            <span>Action Required — Your Appraisal</span>
                        </div>
                        <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">
                            Complete your self-evaluation for {myPending.cycle_name || "the current cycle"}
                        </h3>
                        <p className="text-xs text-muted-foreground mt-0.5">
                            Current status: {myPending.status.replaceAll("_", " ")}
                        </p>
                    </div>
                    <Button
                        size="sm"
                        variant="brand"
                        onClick={() => navigate(`/appraisal/my-appraisal/${myPending.id}`)}
                    >
                        Open My Appraisal <ArrowRight className="w-4 h-4 ml-1" />
                    </Button>
                </div>
            ) : (
                mineList.length === 0 && (
                    <DashboardSection>
                        <p className="text-sm text-muted-foreground">
                            You don't have an appraisal yet for the current cycle.
                        </p>
                    </DashboardSection>
                )
            )}

            {/* ── RM section — team aggregations. Only when the user manages a team. ── */}
            {isRM && teamList.length > 0 && (
                <RMTeamSummary appraisals={teamList} />
            )}

            {/* ── HR section — org-wide aggregations. Only for HR / admin. ── */}
            {isHR && (
                <HRGovernanceSummary appraisals={allList} />
            )}

            {/* Quick actions strip — visible to every role, role-aware buttons. */}
            <DashboardSection title="Quick Actions">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => navigate("/appraisal/my-appraisal")}
                        className="justify-start"
                    >
                        <ClipboardList className="w-4 h-4 mr-2" /> My Appraisal
                    </Button>
                    {isRM && (
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => navigate("/appraisal/team-review")}
                            className="justify-start"
                        >
                            <ClipboardList className="w-4 h-4 mr-2" /> Team Review
                        </Button>
                    )}
                    {(isRM || isHR) && (
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => navigate("/appraisal/kpi-manager")}
                            className="justify-start"
                        >
                            <ListChecks className="w-4 h-4 mr-2" /> Manage KPIs
                        </Button>
                    )}
                    {isHR && (
                        <>
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => navigate("/appraisal/cycles/new")}
                                className="justify-start"
                            >
                                <CalendarClock className="w-4 h-4 mr-2" /> Create Cycle
                            </Button>
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => navigate("/appraisal/final-approval")}
                                className="justify-start"
                            >
                                <CheckCircle2 className="w-4 h-4 mr-2" /> Final Approval
                            </Button>
                        </>
                    )}
                </div>
            </DashboardSection>
        </DashboardShell>
    );
};

export default AppraisalDashboard;
